import {RefObject, useEffect, useRef} from 'react';

import useIsomorphicLayoutEffect from './useIsomorphicLayoutEffect';

/**
 * TODO: use real types instead of any when Typescript is updated
 * @see {@link https://github.com/juliencrn/usehooks-ts/blob/master/src/useEventListener/useEventListener.ts}
 */
function useEventListener<
	KW extends keyof any,
	KH extends keyof any,
	KM extends keyof any,
	T extends HTMLElement | MediaQueryList | void = void,
>(
	eventName: KW | KH | KM,
	handler: (event: any[KW] | any[KH] | any[KM] | Event) => void,
	element?: RefObject<T>,
	options?: boolean | any,
) {
	// Create a ref that stores handler
	const savedHandler = useRef(handler);

	useIsomorphicLayoutEffect(() => {
		savedHandler.current = handler;
	}, [handler]);

	useEffect(() => {
		// Define the listening target
		const targetElement: T | Window = element?.current ?? window;

		if (!(targetElement && targetElement.addEventListener)) {
			return undefined;
		}

		// Create event listener that calls handler function stored in ref
		const listener: typeof handler = (event) => savedHandler.current(event);

		targetElement.addEventListener(eventName as string, listener, options);

		// Remove event listener on cleanup
		return () => {
			targetElement.removeEventListener(eventName as string, listener, options);
		};
	}, [eventName, element, options]);
}

export default useEventListener;
